import { Flex, Spinner } from "@chakra-ui/react";
import { useEffect } from "react";
import { ContentSectionCustomCard, NavigationSource } from "../../types";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { CUSTOM_CARD_TYPES } from "../../components/cards/constants";
import { FeatureCardContainer } from "../../components/cards/FeatureCardContainer";
import { useQuery } from "@tanstack/react-query";
import { getSection } from "../root/api/rootScreenApi";
import { ExpandedContentHeader } from "./components/ExpandedContentHeader";
import { ExpandedContentGrid } from "./components/ExpandedContentGrid";
import { CollapsibleNavBar } from "../../components/navbar/CollapsibleNavBar";
import { useScrollVisibility } from "../../hooks/useScrollVisibility";
import { useEntityCache } from "../../hooks/useEntityCache";
import { useTopBarHeight } from "../../hooks/userTopBarHeight";
import { useVirtualScrollRestoration } from "../../hooks/useVirtualScrollRestoration";

export function ExpandedContentScreen() {
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const topBarHeight = useTopBarHeight();

  // Scroll restoration for back navigation
  const { onVisibleIndexChange, restoreToIndex } = useVirtualScrollRestoration();

  // Read navigation source from React Router state
  const navigationSource = location.state as NavigationSource | null;

  const { saveEntityToCache } = useEntityCache();

  const { data: sectionData, isLoading } = useQuery({
    enabled: !!sectionId,
    queryKey: ["section", sectionId],
    queryFn: async () => {
      if (!sectionId) return null;
      const response = await getSection(sectionId);
      const results = response?.data.payload.results;

      // Cache all entities
      if (results) {
        [
          ...(results?.articles ?? []),
          ...(results?.artists ?? []),
          ...(results?.comics ?? []),
          ...(results?.releases ?? []),
          ...(results?.cards ?? []),
          ...(results?.bundles ?? []),
        ].forEach(saveEntityToCache);
      }

      return results;
    },
  });

  const section = sectionData;
  const data = [
    ...(section?.articles ?? []),
    ...(section?.artists ?? []),
    ...(section?.comics ?? []),
    ...(section?.releases ?? []),
    ...(section?.cards ?? []),
    ...(section?.bundles ?? []),
  ];

  const style = section?.meta.layout.style;
  const { context } = style ?? {};

  const { isHidden } = useScrollVisibility();

  useEffect(() => {
    if (!sectionId) {
      navigate("/");
    }
  }, [sectionId, navigate]);

  const isFeatureCard =
    context === CUSTOM_CARD_TYPES.featuredCarousel ||
    context === CUSTOM_CARD_TYPES.featuredSeriesRoadblock;

  const isRoadblock = context === CUSTOM_CARD_TYPES.featuredSeriesRoadblock;

  return (
    <Flex
      direction="column"
      gap={isSmallBreakpoint ? 0 : 6}
      width="full"
      sx={{
        WebkitOverflowScrolling: "touch",
      }}
    >
      {section && !isLoading ? (
        <>
          {isFeatureCard ? (
            <>
              <ExpandedContentHeader
                section={section}
                data={data}
                navigationSource={navigationSource}
              />
              <FeatureCardContainer
                data={data as ContentSectionCustomCard[]}
                scrollByCount={1}
                isRoadblock={isRoadblock}
              />
            </>
          ) : (
            <ExpandedContentGrid
              section={section}
              data={data}
              header={
                <CollapsibleNavBar
                  isCollapsed={isHidden}
                  shouldCollapse={isSmallBreakpoint}
                  boxProps={{
                    paddingRight: isSmallBreakpoint ? "0px" : "40px",
                    position: "sticky",
                    top: `${topBarHeight.get()}px`,
                    zIndex: 1,
                    bg: "charcoal.charcoal",
                  }}
                >
                  <ExpandedContentHeader
                    section={section}
                    data={data}
                    navigationSource={navigationSource}
                  />
                </CollapsibleNavBar>
              }
              onVisibleIndexChange={onVisibleIndexChange}
              restoreToIndex={restoreToIndex}
            />
          )}
        </>
      ) : (
        <Flex
          width="100%"
          height="100vh"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner color="blaze.blaze" />
        </Flex>
      )}
    </Flex>
  );
}
